import { memo } from 'react'

import { F0Box, F0TagStatus, F0Text } from '@factorialco/f0-react'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { ApprovalsV2ApprovalTimelineEntryFragment } from 'generated/resources/graphql'

import AssigneesRow from './AssigneesRow'
import TimelineItemWrapper, {
  FutureIcon,
  getContentIcon,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getStatusType,
} from './TimelineItemWrapper'
import type { ItemStatus, TimelineItemEmployeeFragment } from './types'
import { STATUS_COLORS } from './types'

interface Props {
  entry: ApprovalsV2ApprovalTimelineEntryFragment
  hasConnectingLine?: boolean
  alert?: React.ReactNode
  daysOverdue?: number
  employees?: TimelineItemEmployeeFragment[]
}

const formatTimestamp = (timestamp: string, isFuture: boolean): string => {
  const date = stringToDate(timestamp, 'backend_datetime')
  const formattedDate = i18nDateToString(date, isFuture ? 'ui' : 'ui_short_with_time')

  return isFuture
    ? i18n.t('approvals_v2.timeline.estimated_on', { date: formattedDate })
    : i18n.t('approvals_v2.timeline.requested_on', { date: formattedDate })
}

const TimelineItem = memo(
  ({ entry, hasConnectingLine = false, alert, daysOverdue, employees = [] }: Props) => {
    const { title, status, time: timestamp, type, actionGroup } = entry
    const isCannotResolve = type === 'cannot_resolve'
    const isEntryOverdue = status === 'running' && daysOverdue && daysOverdue > 0
    const displayStatus: ItemStatus = isEntryOverdue ? 'overdue' : status
    const statusLabel = getStatusLabel(displayStatus)
    const statusType = getStatusType(displayStatus)
    const statusIcon = isCannotResolve ? <FutureIcon /> : getStatusIcon(displayStatus)
    const statusColor = isCannotResolve ? STATUS_COLORS.default : getStatusColor(displayStatus)
    const contentIcon = getContentIcon(type, actionGroup)

    if (isCannotResolve) {
      return (
        <TimelineItemWrapper
          statusIcon={statusIcon}
          contentIcon={contentIcon}
          hasConnectingLine={hasConnectingLine}
          statusColor={statusColor}
        >
          <F0Text variant="body" content={i18n.t('approvals_v2.timeline.cannot_resolve.title')} />
          <F0Text
            variant="description"
            content={i18n.t('approvals_v2.timeline.cannot_resolve.description')}
          />
        </TimelineItemWrapper>
      )
    }

    return (
      <TimelineItemWrapper
        statusIcon={statusIcon}
        contentIcon={contentIcon}
        hasConnectingLine={hasConnectingLine}
        statusColor={statusColor}
        alert={alert}
      >
        <F0Box display="flex" flexDirection="column" gap="lg">
          <F0Box display="flex" flexDirection="column" gap="sm">
            <F0Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="md"
              justifyContent="between"
            >
              <F0Text variant="body" content={title} />
              <F0TagStatus text={statusLabel} variant={statusType} />
            </F0Box>
            <F0Text
              variant="description"
              content={formatTimestamp(timestamp, status === 'future')}
            />
          </F0Box>
          <AssigneesRow
            assignees={entry.assignees ?? []}
            employees={employees}
            totalAssigneesCount={entry.totalAssigneesCount}
          />
        </F0Box>
      </TimelineItemWrapper>
    )
  }
)

TimelineItem.displayName = 'TimelineItem'

export default TimelineItem
